import { ActionIcon, Card, Group, LoadingOverlay, Text, Tooltip, Transition } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { notifications, showNotification } from '@mantine/notifications';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getRobot, APIRobotInfo, useBot, useChatStore, useMedia } from '@mygpt/app';

import Chat from '@/components/Chat';
import { getAdminWebSocket } from '@/components/Notification/hooks/adminWebsocket';
import { useFilePreviewStore } from '@mygpt/app/chat/stores/useFilePreviewStore';
import { useFileViewerStore } from '@mygpt/app/chat/stores/useFileViewerStore';
import { PDFViewer } from '@mygpt/app/chat/components/PDFViewer';
import { FileViewer } from '@mygpt/app/chat/components/FileViewer';

const ChatPage = () => {
  const { botId } = useBot();
  const { shownCanvas, clearShownCanvas } = useChatStore();
  const { isMobile } = useMedia();
  const { file_id } = useFilePreviewStore();
  // 角标（PDFViewer + 高亮）和正文文件卡片（FileViewer 纯预览）共用右侧分栏，互斥打开
  const { file_id: viewerFileId, close: closeFileViewer } = useFileViewerStore();
  const [loading, setLoading] = useState(true);
  const [robotInfo, setRobotInfo] = useState<APIRobotInfo>();
  const webSocketClient = getAdminWebSocket();
  const { t } = useTranslation();
  const { clearReferenceInfo } = useFilePreviewStore();

  const getRobotInfo = async () => {
    let data = robotInfo;
    if (!data && botId) {
      const res = await getRobot({ aiId: botId });
      data = res.data;
    }
    if (!data) return;
    setRobotInfo(data);
    setLoading(false);
  };

  useEffect(() => {
    getRobotInfo();
  }, [botId]);

  const handleWebSocketMessage = (message: any) => {
    if (message.type !== 'notification') return;

    switch (message.event) {
      case 'still_learning':
        showNotification({
          id: botId,
          color: 'yellow',
          autoClose: false,
          withCloseButton: false,
          message: t('dataset.still_learning'),
        });
        break;
      default:
        break;
    }
  };

  const onlyOnceRef = useRef(false);
  useEffect(() => {
    if (!onlyOnceRef.current) {
      webSocketClient.send({
        type: 'notification',
        event: 'fetch_still_learning_status',
        data: {
          botId,
        },
      });

      onlyOnceRef.current = true;
    }

    webSocketClient.on('message', handleWebSocketMessage);

    return () => {
      webSocketClient.off('message', handleWebSocketMessage);
      notifications.hide(botId);
    };
  }, [botId, webSocketClient, handleWebSocketMessage]);

  const isFilePreviewOpen = (file_id || viewerFileId) && !isMobile;

  useEffect(() => {
    return () => {
      // 当用户离开后台聊天页面，清理引用高亮数据和文件预览
      clearReferenceInfo();
      closeFileViewer();
    };
  }, []);

  return (
    <div className="h-full" style={{ position: 'relative', overflow: 'hidden', display: 'flex' }}>
      <div
        className="transition-all"
        style={{
          position: 'relative',
          minHeight: '80vh',
          flex: isFilePreviewOpen ? '1' : '1 1 100%',
          overflow: 'hidden',
        }}
      >
        <LoadingOverlay visible={loading} />
        {robotInfo && <Chat robotInfo={robotInfo} />}
      </div>
      {isFilePreviewOpen && (file_id ? <PDFViewer embedded={true} /> : <FileViewer embedded={true} />)}
    </div>
  );
};

export default ChatPage;
